import { useMemo, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Info, Lightbulb, AlertTriangle, ExternalLink } from 'lucide-react';
import { Markdown } from './Markdown';

/**
 * One shared citation shape. Agents (`ai_runs.citations[]`) return
 * `{ kind, id, note? }`; some callers pass a human label. `label`/`note`
 * are interchangeable here so both flow straight in.
 */
export interface AiCitation {
  kind: string;
  id: string;
  label?: string;
  note?: string;
}

export type AiTone = 'neutral' | 'note' | 'recommendation' | 'warning';

/**
 * `<AiOutput>` is the single, finance-grade renderer for every AI/LLM text
 * surface in the app. It wraps `<Markdown>` (which already escapes raw HTML —
 * we deliberately do NOT add rehype-raw, so no tag can ever leak) and layers
 * on the things a #1 product needs everywhere AI speaks:
 *
 *   - citation pills      — clickable chips that deep-link to the source record
 *   - copy-to-clipboard   — one click to lift the answer
 *   - callouts            — `> [!NOTE] / [!WARNING] / [!RECOMMENDATION]`
 *                           blockquotes render as toned cards; a `tone` prop
 *                           can also wrap the whole block.
 *   - finance tables      — tabular-nums + right-aligned numerics + zebra
 *                           (handled in Markdown via the shared table styles).
 */

// Map a citation `kind` to the in-app route segment under the workspace.
// Anything not here renders as a plain (non-link) label chip.
const KIND_TO_PAGE: Record<string, string> = {
  ap_invoice: 'ap-invoices',
  ar_invoice: 'ar-invoices',
  vendor: 'vendors',
  expense: 'expenses',
  payment: 'payments',
  payment_run: 'payments',
  budget: 'budgets',
  journal: 'ledger',
  ledger: 'ledger',
  account: 'ledger',
  purchase_order: 'matching',
  po: 'matching',
  delivery: 'matching',
};

function CalloutCard({ tone, children }: { tone: Exclude<AiTone, 'neutral'>; children: ReactNode }) {
  const { t } = useTranslation();
  const cfg = {
    note: {
      Icon: Info,
      label: t('aiOutput.callout.note'),
      cls: 'border-accent-300 bg-accent-50 dark:border-accent-800 dark:bg-accent-900/20',
      icon: 'text-accent-600 dark:text-accent-300',
    },
    recommendation: {
      Icon: Lightbulb,
      label: t('aiOutput.callout.recommendation'),
      cls: 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/20',
      icon: 'text-emerald-600 dark:text-emerald-300',
    },
    warning: {
      Icon: AlertTriangle,
      label: t('aiOutput.callout.warning'),
      cls: 'border-warn-300 bg-warn-50 dark:border-warn-800 dark:bg-warn-900/20',
      icon: 'text-warn-600 dark:text-warn-300',
    },
  }[tone];
  const { Icon } = cfg;
  return (
    <div className={`my-2 rounded-lg border px-3 py-2.5 ${cfg.cls}`}>
      <div className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide mb-1 ${cfg.icon}`}>
        <Icon size={13} />
        {cfg.label}
      </div>
      <div className="text-sm text-ink-700 dark:text-ink-200">{children}</div>
    </div>
  );
}

// Recognise a callout lead marker at the start of the text and split it off so
// the rest renders as normal markdown inside a toned card. Supports both the
// GFM admonition syntax (`> [!NOTE]`) and a leading "Recommendation:" pattern.
function detectTone(text: string): { tone: Exclude<AiTone, 'neutral'> | null; body: string } {
  const trimmed = text.trimStart();
  const admonition = /^>?\s*\[!(NOTE|WARNING|RECOMMENDATION|TIP|IMPORTANT|CAUTION)\]\s*/i.exec(trimmed);
  if (admonition) {
    const kind = admonition[1].toUpperCase();
    const tone: Exclude<AiTone, 'neutral'> =
      kind === 'WARNING' || kind === 'CAUTION' || kind === 'IMPORTANT'
        ? 'warning'
        : kind === 'TIP' || kind === 'RECOMMENDATION'
          ? 'recommendation'
          : 'note';
    // Drop the marker and any leading blockquote markers from each line.
    const body = trimmed.slice(admonition[0].length).replace(/^>\s?/gm, '');
    return { tone, body };
  }
  if (/^>?\s*recommendation[:：]/i.test(trimmed)) {
    const body = trimmed.replace(/^>?\s*recommendation[:：]\s*/i, '').replace(/^>\s?/gm, '');
    return { tone: 'recommendation', body };
  }
  return { tone: null, body: text };
}

function CitationPill({ c, wsId }: { c: AiCitation; wsId?: string }) {
  const label = c.label || c.note || `${c.kind} ${c.id.slice(0, 8)}`;
  const page = KIND_TO_PAGE[c.kind];
  const className =
    'inline-flex items-center gap-1 max-w-[16rem] truncate rounded-full border border-ink-200 ' +
    'dark:border-ink-700 bg-ink-50 dark:bg-ink-900 px-2 py-0.5 text-[11px] text-ink-600 ' +
    'dark:text-ink-300 hover:border-accent-400 hover:text-accent-700 dark:hover:text-accent-200 transition-colors';
  if (page && wsId) {
    return (
      <a href={`/app/workspace/${wsId}/${page}`} className={className} title={label}>
        <ExternalLink size={10} className="shrink-0" />
        <span className="truncate">{label}</span>
      </a>
    );
  }
  return (
    <span className={className.replace('hover:border-accent-400 hover:text-accent-700 dark:hover:text-accent-200 transition-colors', '')} title={label}>
      <span className="truncate">{label}</span>
    </span>
  );
}

export function AiOutput({
  children,
  citations,
  tone = 'neutral',
  copyable = true,
  className,
}: {
  /** The raw AI markdown text. */
  children: string;
  /** `ai_runs.citations[]` (or any label chips) to render as deep-link pills. */
  citations?: AiCitation[];
  /** Force-wrap the whole block in a toned callout (overrides auto-detect). */
  tone?: AiTone;
  /** Show the copy-to-clipboard button (default true). */
  copyable?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const [copied, setCopied] = useState(false);

  const text = typeof children === 'string' ? children : String(children ?? '');
  const detected = useMemo(() => (tone === 'neutral' ? detectTone(text) : { tone: null, body: text }), [text, tone]);
  const effectiveTone: Exclude<AiTone, 'neutral'> | null =
    tone !== 'neutral' ? tone : detected.tone;
  const body = detected.body;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable — silently no-op */
    }
  };

  const inner =
    effectiveTone ? (
      <CalloutCard tone={effectiveTone}>
        <Markdown>{body}</Markdown>
      </CalloutCard>
    ) : (
      <Markdown>{body}</Markdown>
    );

  return (
    <div className={`relative group ${className ?? ''}`}>
      {copyable && text.trim() && (
        <button
          type="button"
          onClick={onCopy}
          aria-label={copied ? t('aiOutput.copied') : t('aiOutput.copy')}
          title={copied ? t('aiOutput.copied') : t('aiOutput.copy')}
          className="absolute top-0 right-0 inline-flex items-center gap-1 rounded-md border border-ink-200 dark:border-ink-700 bg-white/80 dark:bg-ink-900/80 px-1.5 py-1 text-[11px] text-ink-500 hover:text-ink-800 dark:hover:text-ink-100 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
        >
          {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
          <span className="hidden sm:inline">{copied ? t('aiOutput.copied') : t('aiOutput.copy')}</span>
        </button>
      )}
      {inner}
      {citations && citations.length > 0 && (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-ink-400 mr-0.5">
            {t('aiOutput.sources')}
          </span>
          {citations.map((c, i) => (
            <CitationPill key={`${c.kind}-${c.id}-${i}`} c={c} wsId={wsId} />
          ))}
        </div>
      )}
    </div>
  );
}
